import { useTranslate } from "ra-core";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";

import type { MailMessage } from "../types";
import { cleanMailHtml, hasBlockedImages, mailDocument } from "./mailHtml";

/**
 * The body of a letter. HTML is cleaned again and shown in an iframe with
 * sandbox (no allow-scripts, no allow-same-origin) and a CSP that loads
 * nothing from outside; remote pictures only after «Показать картинки».
 * «Текстом» shows the plain text instead.
 */
export const MailBody = ({ mail }: { mail: MailMessage }) => {
  const translate = useTranslate();
  const html = mail.body_html?.trim() ? mail.body_html : null;
  const [asText, setAsText] = useState(!html);
  const [showImages, setShowImages] = useState(false);
  const srcDoc = useMemo(
    () => (html ? mailDocument(html, { showImages }) : ""),
    [html, showImages],
  );
  const blocked = useMemo(
    () => (html ? hasBlockedImages(cleanMailHtml(html)) : false),
    [html],
  );

  return (
    <div className="flex flex-col gap-2 min-w-0">
      {html ? (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setAsText(!asText)}
          >
            {translate(asText ? "crm.mail.as_html" : "crm.mail.as_text")}
          </Button>
          {blocked && !asText && !showImages ? (
            <>
              <span className="text-muted-foreground">
                {translate("crm.mail.images_blocked")}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setShowImages(true)}
              >
                {translate("crm.mail.show_images")}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      {asText || !html ? (
        <div
          className="whitespace-pre-wrap break-words text-sm rounded-md bg-muted/50 p-2 max-h-80 overflow-y-auto"
          data-mail-text
        >
          {mail.body_text ?? ""}
        </div>
      ) : (
        <iframe
          title={mail.subject || translate("crm.mail.no_subject")}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          srcDoc={srcDoc}
          className="w-full h-72 rounded-md border bg-white"
          data-mail-frame
        />
      )}
    </div>
  );
};
